// src/pages/Home.js

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faBolt,
  faCameraRetro,
  faChalkboardTeacher,
  faCheckCircle,
  faClipboardCheck,
  faGlobe,
  faLock,
  faMobileAlt,
  faSchool,
  faShareAlt,
  faUserShield,
  faUserTie,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { ROUTES } from "../config";
import "../styles/Home.css";

const workflowSteps = [
  {
    icon: faCameraRetro,
    title: "Capture",
    text:
      "Teachers and principals take photos or short videos in the app, then add simple context like a title, story or class.",
  },
  {
    icon: faClipboardCheck,
    title: "Review",
    text:
      "Uploads arrive in one principal-friendly review queue, so the school can approve what is ready before families see it.",
  },
  {
    icon: faShareAlt,
    title: "Share",
    text:
      "Approved moments can appear in the parent feed and, where enabled by the school, on selected publishing channels.",
  },
];

const schoolBenefits = [
  {
    icon: faUserTie,
    title: "For principals",
    text:
      "Keep oversight without becoming the school photo bottleneck. Review, approve, invite staff and manage school settings from one place.",
  },
  {
    icon: faChalkboardTeacher,
    title: "For teachers",
    text:
      "Capture the moment while it is happening. No chasing folders, renaming files, resizing images or sending media through personal chats.",
  },
  {
    icon: faUsers,
    title: "For parents",
    text:
      "Give families a clearer place to view approved school memories, without mixing school media into public social feeds by default.",
  },
];

const trustPoints = [
  "Role-based access for principals, staff and parents",
  "Principal review before school media is shared",
  "School-controlled parent invites and staff access",
  "Designed around secure, school-managed photo workflows",
];

const Home = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      setScrollY(y);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const elements = document.querySelectorAll(".reveal-on-scroll");
    if (!elements.length) return undefined;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            obs.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.18,
        rootMargin: "0px 0px -80px 0px",
      }
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="home-container" style={{ "--scrollY": `${scrollY}px` }}>
      <Helmet>
        <title>GhrianGhraffy - School Photos Made Easier for Irish Schools</title>
        <meta
          name="description"
          content="GhrianGhraffy helps Irish schools capture, review and share school photos and videos through a simple principal-approved workflow for staff and parents."
        />
        <meta
          name="keywords"
          content="school photo app Ireland, Irish primary schools, principal photo approval, teacher photo upload, secure school photos, school video sharing, parent school photos"
        />
      </Helmet>

      <main>
        <header className="home-hero">
          <div className="home-hero__glow" aria-hidden="true" />
          <div className="home-hero__inner">
            <section className="home-hero__copy" aria-labelledby="home-hero-title">
              <div className="home-pill-row">
                <span className="home-pill">Built for schools in Ireland</span>
                <span className="home-pill home-pill--soft">Principal-approved sharing</span>
              </div>

              <h1 id="home-hero-title">School photos, without the admin headache.</h1>

              <p className="home-hero__lead">
                GhrianGhraffy helps teachers capture school moments, gives principals a clear
                review step, and lets parents view approved photos and videos in one secure place.
              </p>

              <div className="home-hero__actions">
                <Link to={ROUTES.PRINCIPAL_REGISTRATION} className="home-button home-button--primary">
                  Start your school setup
                </Link>
                <Link to={ROUTES.LEARN_MORE} className="home-button home-button--secondary">
                  See how it works
                </Link>
              </div>

              <div className="home-hero__reassurance" aria-label="Key benefits">
                <span>
                  <FontAwesomeIcon icon={faBolt} /> Less admin for busy staff
                </span>
                <span>
                  <FontAwesomeIcon icon={faLock} /> Clearer control for schools
                </span>
              </div>
            </section>

            <section className="home-hero__visual" aria-label="GhrianGhraffy workflow preview">
              <div className="phone-preview">
                <div className="phone-preview__topbar">
                  <span />
                  <strong>Today at school</strong>
                  <span />
                </div>

                <div className="phone-preview__camera-card">
                  <div className="phone-preview__photo">
                    <FontAwesomeIcon icon={faCameraRetro} />
                  </div>
                  <div>
                    <p className="phone-preview__label">Sports Day</p>
                    <p className="phone-preview__sub">3 photos uploaded by a teacher</p>
                  </div>
                </div>

                <div className="phone-preview__status-card phone-preview__status-card--pending">
                  <FontAwesomeIcon icon={faClipboardCheck} />
                  <div>
                    <strong>Awaiting principal review</strong>
                    <span>Choose what parents can see</span>
                  </div>
                </div>

                <div className="phone-preview__status-card phone-preview__status-card--approved">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <div>
                    <strong>Approved</strong>
                    <span>Visible in the parent feed</span>
                  </div>
                </div>
              </div>

              <div className="home-float-note home-float-note--top">
                <FontAwesomeIcon icon={faBell} />
                <span>New staff upload</span>
              </div>
              <div className="home-float-note home-float-note--bottom">
                <FontAwesomeIcon icon={faUserShield} />
                <span>Principal stays in control</span>
              </div>
            </section>
          </div>
        </header>

        <section className="home-proof-strip" aria-label="GhrianGhraffy summary">
          <div className="home-proof-strip__inner">
            <p>
              A simpler school photo workflow: staff capture moments, principals approve them, and
              families see approved memories without ad-hoc sharing or extra admin.
            </p>
          </div>
        </section>

        <section className="home-section home-section--light" id="why">
          <div className="home-section__inner home-split">
            <div className="home-split__copy reveal-on-scroll">
              <p className="home-eyebrow">Why schools need it</p>
              <h2>School photo sharing should not depend on scattered phones and folders.</h2>
              <p>
                In a busy school week, photos can end up in personal camera rolls, staff messages,
                shared folders, website inboxes and social media drafts. GhrianGhraffy brings that
                work into one school-managed flow.
              </p>
            </div>

            <div className="home-problem-list reveal-on-scroll">
              <article>
                <FontAwesomeIcon icon={faMobileAlt} />
                <div>
                  <h3>Teachers need speed</h3>
                  <p>Take the photo, add context, upload it, and get back to teaching.</p>
                </div>
              </article>
              <article>
                <FontAwesomeIcon icon={faUserShield} />
                <div>
                  <h3>Principals need oversight</h3>
                  <p>Review what is shared before it reaches parents or wider destinations.</p>
                </div>
              </article>
              <article>
                <FontAwesomeIcon icon={faSchool} />
                <div>
                  <h3>Schools need consistency</h3>
                  <p>Use the same process for everyday classroom moments and bigger school events.</p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="home-section" id="workflow">
          <div className="home-section__inner">
            <div className="home-section__header">
              <p className="home-eyebrow">How it works</p>
              <h2>Three clear steps, from classroom moment to approved memory.</h2>
              <p>
                The app is designed around the way schools already work: teachers are busy,
                principals need a clear review point, and parents want a simple place to view what
                has been approved.
              </p>
            </div>

            <div className="home-workflow-grid">
              {workflowSteps.map((step, index) => (
                <article className="home-workflow-card reveal-on-scroll" key={step.title}>
                  <div className="home-workflow-card__number">{index + 1}</div>
                  <div className="home-card-icon">
                    <FontAwesomeIcon icon={step.icon} />
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="home-section home-section--blue" id="for-schools">
          <div className="home-section__inner">
            <div className="home-section__header home-section__header--left">
              <p className="home-eyebrow">Made for real school days</p>
              <h2>Less chasing. Less copying. Less uncertainty.</h2>
              <p>
                GhrianGhraffy is not trying to turn schools into media teams. It is there to remove
                the little admin jobs that appear every time someone wants to share school life.
              </p>
            </div>

            <div className="home-audience-grid">
              {schoolBenefits.map((item) => (
                <article className="home-audience-card reveal-on-scroll" key={item.title}>
                  <div className="home-card-icon home-card-icon--blue">
                    <FontAwesomeIcon icon={item.icon} />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="home-section home-section--light" id="trust">
          <div className="home-section__inner home-trust-layout">
            <div className="home-trust-card reveal-on-scroll">
              <p className="home-eyebrow">School-controlled sharing</p>
              <h2>Designed to make the safer workflow the easiest workflow.</h2>
              <p>
                GhrianGhraffy keeps the important decisions with the school. Staff can upload
                quickly, but approved visibility and sharing remain controlled through the school
                workflow.
              </p>

              <ul className="home-check-list">
                {trustPoints.map((point) => (
                  <li key={point}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="home-feature-stack reveal-on-scroll">
              <article>
                <FontAwesomeIcon icon={faGlobe} />
                <h3>Website-ready when you are</h3>
                <p>
                  Schools can choose website integration when it suits them. It is optional, guided,
                  and separate from the core photo workflow.
                </p>
              </article>
              <article>
                <FontAwesomeIcon icon={faShareAlt} />
                <h3>Optional publishing destinations</h3>
                <p>
                  Sharing options are controlled by the school, so approved content does not have to
                  be copied manually from one place to another.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="home-section" id="setup">
          <div className="home-section__inner home-setup-band reveal-on-scroll">
            <div>
              <p className="home-eyebrow">Simple setup</p>
              <h2>Start with the essentials. Add the extras later.</h2>
              <p>
                Principals can begin with the core workflow first: invite staff, review uploads and
                share approved content with parents. Website and social publishing settings can be
                configured when the school is ready.
              </p>
            </div>
            <Link to={ROUTES.PRINCIPAL_REGISTRATION} className="home-button home-button--primary">
              Register your school
            </Link>
          </div>
        </section>

        <section className="home-section home-final-cta">
          <div className="home-section__inner">
            <div className="home-final-cta__card reveal-on-scroll">
              <p className="home-eyebrow">Ready for easier school photos?</p>
              <h2>Give your school one clear place for photos, videos and approval.</h2>
              <p>
                Start your Principal registration or contact us if you would like to talk through the
                setup first.
              </p>
              <div className="home-hero__actions home-hero__actions--center">
                <Link to={ROUTES.PRINCIPAL_REGISTRATION} className="home-button home-button--primary">
                  Start your school setup
                </Link>
                <a href="mailto:information@ghrianghraffy.com" className="home-button home-button--secondary">
                  Talk to us
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="home-footer">
        <div className="home-footer__inner">
          <p>&copy; {new Date().getFullYear()} GhrianGhraffy. All rights reserved.</p>
          <div className="home-footer__links">
            <Link to={ROUTES.TERMS}>Terms</Link>
            <Link to={ROUTES.PRIVACY}>Privacy</Link>
            <Link to={ROUTES.DELETE_ACCOUNT}>Delete Account/Data</Link>
            <Link to={ROUTES.FAQ}>FAQ</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
